import { ref, computed, reactive } from "vue";
import axios from "axios";

export function useDashboardStatistics() {
  const colors = [
    "#3b82f6",
    "#d32f2f",
    "#fbc02d",
    "#689f38",
    "#8E44AD",
    "#95A5A6",
    "#16A085",
    "#2980B9",
    "#D35400",
    "#2C3E50",
    "#7F8C8D",
    "#BDC3C7",
    "#9B59B6",
    "#3498DB",
    "#2ECC71",
    "#1ABC9C",
    "#F39C12",
    "#E67E22",
    "#ECF0F1",
    "#C0392B",
    "#D98880",
    "#F1948A",
    "#C39BD3",
    "#BB8FCE",
    "#7D3C98",
    "#76D7C4",
    "#73C6B6",
    "#F7DC6F",
    "#F8C471",
    "#E59866",
    "#D7DBDD",
    "#85929E",
    "#34495E",
    "#17202A",
  ];

  const chartData = reactive({
    labels: ["UFIs"],
    datasets: [
      {
        data: [0],
        backgroundColor: colors,
        hoverBackgroundColor: colors,
      },
    ],
  });

  const chartOptions = {
    legend: {
      labels: {
        fontColor: "#495057",
      },
    },
  };

  const chartLabels = ref([]);
  const sumarios = reactive({ pendientes: 0, vencidos: 0 });
  const expedientes = reactive({ pendientes: 0, vencidos: 0 });
  const UFIs = reactive({ pendientes: 0, vencidos: 0 });
  const preliminares = reactive({ pendientes: 0, vencidos: 0 });
  const allActuations = reactive({ pendientes: 0, vencidos: 0 });
  const inputSwitchActuationStatus = ref(false);
  const showProgressSpinner = ref(false);

  const policeCardsData = [
    { cardName: "Sumarios", statisticsName: "sumarios", color: "primary" },
    { cardName: "Expedientes", statisticsName: "expedientes", color: "danger" },
    { cardName: "UFI", statisticsName: "UFIs", color: "warning" },
    { cardName: "Preliminares", statisticsName: "preliminares", color: "success" },
  ];

  const UFIsCardsDataStatisticsName = "allActuations";
  const UFIsCardsData = {
    "ufi-anivi": { cardName: "UFI - ANIVI", color: "primary" },
    "ufi-cavig": { cardName: "UFI - CAVIG", color: "danger" },
    "ufi-flagrancia": { cardName: "UFI -  FLAGRANCIA", color: "success" },
    "ufi-delitos-especiales": { cardName: "UFI - D. ESPECIALES", color: "primary" },
    "ufi-delitos-informaticos-y-estafas": {
      cardName: "UFI - D. INFOR. Y ESTAFAS",
      color: "danger",
    },
    "ufi-generica": { cardName: "UFI - GENÉRICA", color: "warning" },
    "ufi-delitos-contra-la-propiedad": {
      cardName: "UFI - D. CONTRA LA PROPIEDAD",
      color: "success",
    },
    "ufis-todas": { cardName: "UFI", statisticsName: "UFIs", color: "warning" },
  };

  const categorizedActuationsNames = {
    "Unidades Fiscales de Investigación Delitos Contra la Propiedad": "Delitos Contra la Propiedad",
    "Unidades Fiscales de Investigación de Delitos Informáticos y Estafas":
      "Delitos Informáticos y Estafas",
    "Unidades Fiscales de Investigación Genérica": "Delitos Genéricos",
    "Unidades Fiscales de Investigacion Flagrancia": "Flagrancia",
    "Unidades Fiscales de Investigacion CAVIG": "Cavig",
    "Unidades Fiscales de Investigacion ANIVI": "Anivi",
    "Unidades Fiscales de Investigacion D.E": "Delitos Especiales",
  };

  const actuationsType = [
    { Sumario: "sumarios" },
    { Expediente: "expedientes" },
    { "Unidades Fiscales de Investigacion": "UFIs" },
    { "Actuacion Preliminar": "preliminares" },
  ];

  async function getData(urls) {
    const pendientes = await axios.get(urls[0], { withCredentials: true });
    const vencidos = await axios.get(urls[1], { withCredentials: true });
    structureActuationsData(pendientes, vencidos);
  }

  function structureActuationsData(pendings, defeateds) {
    allActuations.pendientes = pendings?.data?.data?.length;
    allActuations.vencidos = defeateds?.data?.data?.length;
    if (pendings.data.data[0]?.claseDeUsuario === "UsuarioFiscal") {
      const UFIsForCategorize = inputSwitchActuationStatus.value
        ? defeateds?.data?.data
        : pendings?.data?.data;
      categorizeByTiposumarioID(UFIsForCategorize);
    } else {
      const actuationsDataObtained = [
        { pendientes: pendings.data.data },
        { vencidos: defeateds.data.data },
      ];
      categorizeByActuationsAmount(actuationsDataObtained);
    }
  }

  function getStatistics(statisticsName) {
    const statsMap = {
      sumarios,
      expedientes,
      UFIs,
      preliminares,
      allActuations,
    };
    return statsMap[statisticsName];
  }

  function categorizeByTiposumarioID(allActuationsData) {
    const categorizedActuations = {};
    allActuationsData.forEach((actuation) => {
      if (!categorizedActuations[actuation.TiposumarioID])
        categorizedActuations[actuation.TiposumarioID] = [];
      categorizedActuations[actuation.TiposumarioID].push(actuation);
    });
    changeTheNamesFromActuations(categorizedActuations);
  }

  function changeTheNamesFromActuations(actuations) {
    const statisticsWithNewNames = {};
    for (const actuationProp in actuations) {
      for (const actuationName in categorizedActuationsNames) {
        if (actuations[actuationProp][0].TiposumarioID.includes(actuationName))
          statisticsWithNewNames[categorizedActuationsNames[actuationName]] =
            actuations[actuationProp];
      }
    }
    getStatisticsLabelsAndValues(statisticsWithNewNames);
  }

  function getStatisticsLabelsAndValues(statistics) {
    const statisticsData = { statisticsLabels: [], statisticsValues: [] };
    for (const prop in statistics) {
      statisticsData.statisticsLabels.push(prop);
      statisticsData.statisticsValues.push(statistics[prop]?.length ?? statistics[prop]);
    }
    if (statisticsData.statisticsValues.length === 0) {
      chartData.datasets[0].data = [0];
      chartData.labels = ["Actuaciones"];
    } else {
      chartData.datasets[0].data = statisticsData.statisticsValues;
      chartData.labels = statisticsData.statisticsLabels;
    }
    showProgressSpinner.value = false;
  }

  function countAmountOfActuations(actuationsDataObtained) {
    actuationsType.map((actuation) => {
      actuationsDataObtained.map((actuationState) => {
        const actuationDataObtainedType = Object.keys(actuationState)[0];
        const actuationName = actuation[Object.keys(actuation)[0]];
        const statsObj = getStatistics(actuationName);
        statsObj[actuationDataObtainedType] = actuationState[actuationDataObtainedType].filter(
          (e) => e.TiposumarioID.includes(Object.keys(actuation)[0])
        ).length;
      });
    });
  }

  function categorizeByActuationsAmount(actuationsDataObtained) {
    countAmountOfActuations(actuationsDataObtained);
    const statistics = {};
    for (const key in policeCardsData) {
      const statistic = getStatistics(policeCardsData[key].statisticsName);
      const statisticsName = policeCardsData[key].statisticsName;
      const statisticsNameCapitalized =
        statisticsName.charAt(0).toUpperCase() + statisticsName.slice(1);
      statistics[statisticsNameCapitalized] =
        statistic[!inputSwitchActuationStatus.value ? "pendientes" : "vencidos"];
    }
    getStatisticsLabelsAndValues(statistics);
  }

  function setDependencia(value) {
    showProgressSpinner.value = true;
    const paramsPendientes = `search={"exp_pre_EstadoActuacion.TipoEstadoActuacionID":[{"operator":"=","value":"En Curso"}]${
      value ? `,"DependenciaID":[{"operator":"=","value":"${value}"}]` : ""
    }}`;
    const paramsVencidos = `search={"exp_pre_EstadoActuacion.TipoEstadoActuacionID":[{"operator":"=","value":"Vencida"}]${
      value ? `,"DependenciaID":[{"operator":"=","value":"${value}"}]` : ""
    }}`;
    const basicUrl = process.env.VUE_APP_BACKEND_ENVIRONMENT;
    getData([
      `${basicUrl}/Actuacion/estadisticas?${paramsPendientes}`,
      `${basicUrl}/Actuacion/estadisticas?${paramsVencidos}`,
    ]);
  }

  return {
    chartData,
    chartOptions,
    chartLabels,
    sumarios,
    expedientes,
    UFIs,
    preliminares,
    allActuations,
    policeCardsData,
    UFIsCardsData,
    UFIsCardsDataStatisticsName,
    inputSwitchActuationStatus,
    showProgressSpinner,
    getData,
    getStatistics,
    setDependencia,
  };
}
